import { governorRegistry } from './GovernorRegistry';

export type GovernorSeverity = 'critical' | 'warning' | 'info';
export type GovernorStatus = 'PASS' | 'FAIL' | 'WARN' | 'UNKNOWN';

export interface GovernorFinding {
  id: string;
  title: string;
  subsystem: string;
  status: GovernorStatus;
  severity: GovernorSeverity;
  evidence?: string;
  rootCause?: string;
  fix?: string;
}

export interface GovernorReport {
  generatedAt: string;
  healthScore: number;
  status: GovernorStatus;
  criticalCount: number;
  warningCount: number;
  unknownCount: number;
  findings: GovernorFinding[];
}

const normalizeModuleFindings = (module: any, raw: any): GovernorFinding[] => {
  const rows = Array.isArray(raw) ? raw : [];
  return rows
    .map((item: any, index: number) => {
      const status = item.status === 'FAILURE' ? 'FAIL' : item.status === 'WARNING' ? 'WARN' : item.status === 'HEALTHY' ? 'PASS' : item.status;
      const title = item.title || item.observation || `${module.name || module.id} check`;
      return {
        id: item.id || `${module.id}-finding-${index + 1}`,
        title,
        subsystem: item.subsystem || module.name || module.id,
        status: status || 'UNKNOWN',
        severity: status === 'FAIL' ? 'critical' : status === 'WARN' || status === 'UNKNOWN' ? 'warning' : 'info',
        evidence: item.evidence || item.observation || module.purpose,
        rootCause: item.rootCause || item.observation || module.purpose,
        fix: item.fix || item.suggestedFix || (status === 'PASS' ? undefined : `Review ${module.name || module.id} audit evidence.`),
      };
    })
    .filter((f: GovernorFinding) => f.status !== 'PASS');
};

const scoreReport = (findings: GovernorFinding[]) => {
  let score = 100;

  for (const f of findings) {
    if (f.status === 'FAIL' && f.severity === 'critical') score -= 20;
    else if (f.status === 'FAIL') score -= 10;
    else if (f.status === 'WARN') score -= 5;
    else if (f.status === 'UNKNOWN') score -= 8;
  }

  return Math.max(0, Math.min(100, score));
};

export async function runGovernorOrchestrator(input: unknown): Promise<GovernorReport> {
  const findings: GovernorFinding[] = [];

  for (const module of governorRegistry) {
    try {
      if (typeof (module as any).run === 'function') {
        const moduleFindings = await (module as any).run(input);
        findings.push(...normalizeModuleFindings(module, moduleFindings));
      } else {
        findings.push({
          id: `governor-module-not-wired-${module.id}`,
          title: `${(module as any).name || module.id} needs live check wiring`,
          subsystem: (module as any).name || module.id,
          status: module.status === 'active' ? 'UNKNOWN' : 'WARN',
          severity: module.priority === 'critical' ? 'warning' : 'info',
          evidence: 'Governor registry module exists but no run() audit function is wired yet.',
          rootCause: 'Registry metadata is active, but executable audit logic is not connected.',
          fix: `Wire a real audit runner for ${module.id}.`,
        });
      }
    } catch (err: any) {
      findings.push({
        id: `governor-module-crash-${module.id.toLowerCase()}`,
        title: `${(module as any).title || (module as any).name || (module as any).id || 'Governor module'} failed`,
        subsystem: module.id,
        status: 'FAIL',
        severity: 'critical',
        evidence: err?.message || String(err),
        rootCause: `The ${module.title} crashed during Governor execution.`,
        fix: `Inspect ${module.title} module wiring, exports, and runtime assumptions.`,
      });
    }
  }

  const criticalCount = findings.filter(f => f.status === 'FAIL' && f.severity === 'critical').length;
  const warningCount = findings.filter(f => f.status === 'WARN' || f.severity === 'warning').length;
  const unknownCount = findings.filter(f => f.status === 'UNKNOWN').length;
  const healthScore = scoreReport(findings);

  return {
    generatedAt: new Date().toISOString(),
    healthScore,
    status: criticalCount > 0 ? 'FAIL' : warningCount > 0 || unknownCount > 0 ? 'WARN' : 'PASS',
    criticalCount,
    warningCount,
    unknownCount,
    findings,
  };
}
